import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { toast } from 'react-toastify';
import { LogIn, Eye, EyeOff } from 'lucide-react';

// Role to module mapping (duplicated from ModuleContext for use before provider is available)
const MULTI_MODULE_ROLES = ['super_admin', 'system_admin'];
const ROLE_MODULE_MAP = {
  super_admin: 'retail', // default module for multi-module roles
  system_admin: 'retail',
  retail_admin: 'retail',
  branch_admin: 'retail',
  branch_staff: 'retail',
  wholesale_admin: 'wholesale',
  admin: 'retail',
  staff: 'retail'
};

// Helper to get redirect path based on user role
const getRedirectPath = (userRole) => {
  // Multi-module roles go to module selection
  if (MULTI_MODULE_ROLES.includes(userRole)) {
    return '/select-module';
  }
  // Single-module roles go directly to their module dashboard
  const module = ROLE_MODULE_MAP[userRole] || 'retail';
  return `/${module}/dashboard`;
};

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const { login } = useAuth();
  const { config: branding, loading: brandingLoading } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();

  // Get intended destination - but we might override based on role
  const intendedDestination = location.state?.from?.pathname;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); // Clear any previous error

    // Basic validation
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        // Check if password change is required
        if (result.requirePasswordChange) {
          navigate('/change-password', { replace: true, state: { firstLogin: true } });
        } else {
          // Determine redirect based on user role
          const userRole = result.user?.role;
          const defaultRedirect = getRedirectPath(userRole);

          // If user was trying to access a specific page, honor that (unless it's just /dashboard)
          const finalRedirect =
            intendedDestination && intendedDestination !== '/dashboard'
              ? intendedDestination
              : defaultRedirect;

          navigate(finalRedirect, { replace: true });
        }
      } else {
        // Login failed - show error message
        setError(result.error || 'Invalid email or password. Please try again.');
      }
    } catch (error) {
      console.error('Login submit error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-black via-primary-charcoal to-primary-slate flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Title */}
        <div className="text-center mb-8">
          {brandingLoading ? (
            <div className="animate-pulse">
              <div className="h-10 bg-gray-700 rounded w-64 mx-auto mb-2"></div>
              <div className="h-5 bg-gray-700 rounded w-48 mx-auto"></div>
            </div>
          ) : (
            <>
              <h1 className="text-4xl font-bold text-accent-gold mb-2">
                {branding?.company_name || 'Eldaved Global'}
              </h1>
              <p className="text-gray-300">Business Management System</p>
            </>
          )}
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-lg shadow-2xl p-8">
          <h2 className="text-2xl font-bold text-primary-black mb-6 text-center">Sign In</h2>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 text-center">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Email */}
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="admin@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent transition-colors"
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent transition-colors"
                  required
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-accent-gold focus:ring-offset-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-primary-black border-t-transparent rounded-full animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>

          {/* Forgot Password Link */}
          <div className="mt-4 text-center">
            <a
              href="/forgot-password"
              className="text-sm text-accent-gold hover:text-accent-goldDark transition-colors"
            >
              Forgot your password?
            </a>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-400 text-sm mt-6">
          © 2026 {branding?.company_name || 'Eldaved Global'}. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
